/*
 * Copyright 2022 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { OverlayArrowContext } from "@/components/vendor/OverlayArrow.tsx";
import { useEnterAnimation } from "@/components/vendor/useAnimation.ts";
import { useRenderProps } from "@/components/vendor/useRenderProps.ts";
import { type TooltipTriggerState, useTooltipTriggerState } from "@/components/vendor/useTooltipTriggerState.ts";
import { FocusableProvider } from "@react-aria/focus";
import { useLayoutEffect } from "@react-aria/utils";
import type { FocusableElement, forwardRefType, RefObject } from "@react-types/shared";
import { createContext, type ForwardedRef, forwardRef, useContext, useRef, useState } from "react";
import { mergeProps, OverlayContainer, useOverlayPosition, useTooltip, useTooltipTrigger } from "react-aria";
import {
    type ContextValue,
    Provider,
    type TooltipProps,
    type TooltipTriggerComponentProps,
    useContextProps,
} from "react-aria-components";

export type { TooltipProps };

export const TooltipTriggerStateContext = createContext<TooltipTriggerState | null>(null);
export const TooltipContext = createContext<ContextValue<TooltipProps, HTMLDivElement>>(null);

/**
 * TooltipTrigger wraps around a trigger element and a Tooltip. It handles opening and closing
 * the Tooltip when the user hovers over or focuses the trigger, and positioning the Tooltip
 * relative to the trigger.
 */
export function TooltipTrigger(props: TooltipTriggerComponentProps) {
    let state = useTooltipTriggerState(props);
    let ref = useRef<FocusableElement>(null);
    let { triggerProps, tooltipProps } = useTooltipTrigger(props, state, ref);

    return (
        <Provider
            values={[
                [TooltipTriggerStateContext, state],
                [TooltipContext, { ...tooltipProps, triggerRef: ref }],
            ]}
        >
            <FocusableProvider {...triggerProps} ref={ref}>
                {props.children}
            </FocusableProvider>
        </Provider>
    );
}

function useExitTimeout(isOpen: boolean, timeout: number): boolean {
    const lastOpen = useRef(isOpen);
    let [isExiting, setExiting] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    function clearTimer() {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;

            isExiting = false;
            setExiting(false);
        }
    }

    if (isOpen) {
        if (!lastOpen.current) {
            lastOpen.current = true;
            clearTimer();
        }
    } else {
        if (lastOpen.current) {
            lastOpen.current = false;

            isExiting = true;
            setExiting(true);

            timer.current = setTimeout(clearTimer, timeout);
        }
    }

    return isExiting;
}

/**
 * A tooltip displays a description of an element on hover or focus.
 */
export const Tooltip = /*#__PURE__*/ (forwardRef as forwardRefType)(function Tooltip(
    { UNSTABLE_portalContainer, ...props }: TooltipProps,
    ref: ForwardedRef<HTMLDivElement>,
) {
    [props, ref] = useContextProps(props, ref, TooltipContext);
    let contextState = useContext(TooltipTriggerStateContext);
    let localState = useTooltipTriggerState(props);
    let state = props.isOpen != null || props.defaultOpen != null || !contextState ? localState : contextState;
    // let isExiting = useExitAnimation(ref, state.isOpen) || props.isExiting || false;
    const isExiting = useExitTimeout(state.isOpen, 500);
    // console.debug("isExiting: %o, isOpen: %o, el: %o", isExiting, state.isOpen, ref.current);
    if (!state.isOpen && !isExiting) {
        return null;
    }

    return (
        <OverlayContainer portalContainer={UNSTABLE_portalContainer}>
            <TooltipInner {...props} tooltipRef={ref} isExiting={isExiting} />
        </OverlayContainer>
    );
});

function TooltipInner(props: TooltipProps & { isExiting: boolean; tooltipRef: RefObject<HTMLDivElement | null> }) {
    let state = useContext(TooltipTriggerStateContext)!;

    // Calculate the arrow size internally
    // Referenced from: packages/@react-spectrum/tooltip/src/TooltipTrigger.tsx
    let arrowRef = useRef<HTMLDivElement>(null);
    let [arrowWidth, setArrowWidth] = useState(0);
    useLayoutEffect(() => {
        if (arrowRef.current && state.isOpen) {
            setArrowWidth(arrowRef.current.getBoundingClientRect().width);
        }
    }, [state.isOpen, arrowRef]);

    let { overlayProps, arrowProps, placement } = useOverlayPosition({
        placement: props.placement || "top",
        targetRef: props.triggerRef!,
        overlayRef: props.tooltipRef,
        offset: props.offset,
        crossOffset: props.crossOffset,
        isOpen: state.isOpen,
        arrowSize: arrowWidth,
        arrowBoundaryOffset: props.arrowBoundaryOffset,
        shouldFlip: props.shouldFlip,
        onClose: () => state.close(true),
    });

    let isEntering = useEnterAnimation(props.tooltipRef, !!placement) || props.isEntering || false;
    let renderProps = useRenderProps({
        ...props,
        defaultClassName: "react-aria-Tooltip",
        values: {
            placement,
            isEntering,
            isExiting: props.isExiting,
            state,
        },
    });

    props = mergeProps(props, overlayProps);
    let { tooltipProps } = useTooltip(props, state);

    return (
        <div
            {...tooltipProps}
            ref={props.tooltipRef}
            {...renderProps}
            style={{ ...overlayProps.style, ...renderProps.style }}
            data-placement={placement ?? undefined}
            data-entering={isEntering || undefined}
            data-exiting={props.isExiting || undefined}
        >
            <OverlayArrowContext.Provider value={{ ...arrowProps, placement, ref: arrowRef }}>
                {renderProps.children}
            </OverlayArrowContext.Provider>
        </div>
    );
}
